import React, { useState, useCallback, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import { WizardSubmissionService } from '../../services/wizardSubmissionService';
import { SmsService } from '../../services/smsService';
import { EmailService } from '../../services/emailService';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';

/**
 * Submit component for final submission of the wizard
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user submits wizard
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const Submit = ({
  stepNumber = 10,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = '',
  description = '',
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [contactPreference, setContactPreference] = useState('email');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!agreedToTerms) {
      alert(t('wizard.submit.errors.agreeToTerms', 'Please agree to the terms and conditions to proceed.'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Mark wizard as completed
      actions.completeWizard();

      // Prepare submission data
      const submissionData = {
        ...state, // Include all wizard state data
        contactPreference,
        timestamp: new Date().toISOString()
      };

      // Add pathway and package selection from review step
      if (state.reviewData) {
        submissionData.selectedPathway = state.reviewData.selectedPathway;
        submissionData.selectedPackage = state.reviewData.selectedPackage;
      }

      console.log('Submitting wizard data:', submissionData);

      // Save to Supabase AND Frappe CRM (with retry logic)
      const savedSubmission = await WizardSubmissionService.saveSubmission(submissionData);
      console.log('Submission saved:', savedSubmission);

      // Log Frappe sync status
      if (savedSubmission.frappe_sync_status === 'synced') {
        console.log('✅ Lead synced to CRM successfully:', savedSubmission.frappe_lead_id);
      } else if (savedSubmission._warning) {
        console.warn('⚠️ CRM sync warning:', savedSubmission._warning);
      }

      // Send welcome SMS
      if (savedSubmission.phone && SmsService.isValidSriLankanPhone(savedSubmission.phone)) {
        console.log('Sending welcome SMS...');
        const smsResult = await SmsService.sendWelcomeSms(savedSubmission);
        if (smsResult.success) {
          console.log('Welcome SMS sent successfully:', smsResult);
        } else {
          console.warn('Failed to send welcome SMS:', smsResult.error);
        }
      } else {
        console.log('No valid phone number found, skipping welcome SMS');
      }

      // Send confirmation email to user and admin
      if (savedSubmission.email) {
        console.log('Sending confirmation email...');
        const emailResult = await EmailService.sendWizardConfirmation(savedSubmission);
        if (emailResult.success) {
          console.log('Confirmation email sent successfully:', emailResult);
        } else {
          console.warn('Failed to send confirmation email:', emailResult.error);
        }
      } else {
        console.log('No email address found, skipping confirmation email');
      }

      // Note: We don't call onNext here since this is the final step
      // The wizard should not advance beyond step 8
      console.log('Wizard completed successfully', {
        completed: true,
        submissionId: savedSubmission.submission_id || savedSubmission.id,
        frappeLeadId: savedSubmission.frappe_lead_id,
        frappeSyncStatus: savedSubmission.frappe_sync_status,
        timestamp: new Date().toISOString(),
        contactPreference
      });

      // Show success state
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting wizard:', error);
      
      // Check if it's a partial success (database saved, CRM failed)
      if (error.partialSuccess) {
        console.warn('Partial success - database saved, CRM sync failed:', error.message);
        // Still show success to user since their data is saved
        // The CRM sync will be retried in background
        setIsSubmitted(true);
        return;
      }
      
      // Full failure - show error
      alert(t('wizard.submit.errors.submitFailed',
        'An error occurred while submitting. Please try again. If the problem persists, contact support.'));
    } finally {
      setIsSubmitting(false);
    }
  }, [agreedToTerms, actions, state, contactPreference, t]);

  const getProfileCompletion = useCallback(() => {
    let completion = 0;
    const maxScore = 100;

    // Country selection (15 points)
    if (state.countrySelection.selectedCountry) completion += 15;

    // Personal info (20 points)
    if (state.personalInfo.firstName && state.personalInfo.lastName) completion += 10;
    if (state.personalInfo.email && state.personalInfo.phone) completion += 10;

    // Education (15 points)
    if (state.education.highestEducation) completion += 8;
    if (state.education.fieldOfStudy) completion += 7;

    // Experience (15 points)
    if (state.experience.length > 0 && state.experience[0].totalExperience) completion += 15;

    // Language skills (15 points)
    if (state.languageSkills.englishSpeaking) completion += 8;
    if (state.languageSkills.hasIELTS === 'yes') completion += 7;

    // Financial info (10 points)
    if (state.financialInfo.educationBudget) completion += 10;

    // Dynamic questions (10 points)
    if (state.countrySelection.answers && Object.keys(state.countrySelection.answers).length > 0) {
      completion += 10;
    }

    return Math.min(completion, maxScore);
  }, [state]);

  const completionPercentage = getProfileCompletion();
  const completionColor = completionPercentage >= 80 ? 'text-green-600' :
    completionPercentage >= 60 ? 'text-yellow-600' : 'text-red-600';
  const defaultTitle = t('wizard.submit.title', 'Submit Your Profile');
  const defaultDescription = t('wizard.submit.description', 'Submit your complete profile to receive personalized immigration recommendations from our experts.');
  const stepTitle = title || defaultTitle;
  const stepDescription = description || defaultDescription;

  // Show success message if submitted
  if (isSubmitted) {
    return (
      <div className={`submit-step w-full mx-auto ${className}`}>
        <div className="text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Profile Submitted Successfully!</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Thank you for completing the Lead Qualification Wizard. Our immigration experts will review your profile
              and contact you within 24-48 hours with personalized recommendations.
            </p>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-green-900 mb-3">What Happens Next?</h3>
            <div className="text-left max-w-2xl mx-auto space-y-3">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium text-green-900">Expert Review</p>
                  <p className="text-sm text-green-800">Our immigration experts will analyze your profile within 24-48 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium text-green-900">Personalized Plan</p>
                  <p className="text-sm text-green-800">You'll receive customized immigration pathways and detailed guidance</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium text-green-900">Contact via {contactPreference === 'email' ? 'Email' : contactPreference === 'phone' ? 'Phone' : 'WhatsApp'}</p>
                  <p className="text-sm text-green-800">We'll reach out using your preferred contact method</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-600 mb-4">
              Have questions? Contact us at{' '}
              <a href="mailto:info@ravanaindustries.com" className="text-blue-600 hover:text-blue-800 underline">
                info@ravanaindustries.com
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`submit-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}{' '}
            - {t('wizard.submit.finalStep', 'Final Step')}
          </span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{stepTitle}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{stepDescription}</p>
      </div>

      {/* Profile Completion Status */}
      <div className="card p-6 sm:p-8 mb-6">
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Profile Completion Status</h3>
          <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - completionPercentage / 100)}`}
                className={`transition-all duration-500 ${completionColor.replace('text', 'stroke')}`}
              />
            </svg>
            <div className="absolute">
              <div className={`text-3xl font-bold ${completionColor}`}>{completionPercentage}%</div>
              <div className="text-sm text-gray-600">Complete</div>
            </div>
          </div>
          <p className="text-gray-700">
            {completionPercentage >= 80 ? 'Excellent! Your profile is ready for submission.' :
              completionPercentage >= 60 ? 'Good progress! Consider adding more details for better recommendations.' :
                'Please complete more sections for accurate assessment.'}
          </p>
        </div>
      </div>

      {/* What Happens Next */}
      <div className="card p-6 sm:p-8 mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">What Happens Next?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Expert Review</h4>
            <p className="text-sm text-gray-600">Our immigration experts will review your profile within 24-48 hours</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Personalized Plan</h4>
            <p className="text-sm text-gray-600">Receive customized immigration pathways and step-by-step guidance</p>
          </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Dedicated Support</h4>
            <p className="text-sm text-gray-600">Get assigned a personal immigration consultant for ongoing assistance</p>
          </div>
        </div>
      </div>

      {/* Contact Preference */}
      <div className="card p-6 sm:p-8 mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">How would you like to be contacted?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { value: 'email', label: 'Email', icon: '📧', description: 'Receive detailed recommendations via email' },
            { value: 'phone', label: 'Phone Call', icon: '📞', description: 'Speak with an immigration consultant directly' },
            { value: 'whatsapp', label: 'WhatsApp', icon: '💬', description: 'Quick updates and responses via WhatsApp' }
          ].map((option) => (
            <label
              key={option.value}
              className={`relative border rounded-lg p-4 cursor-pointer transition-all ${contactPreference === option.value
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
            >
              <input
                type="radio"
                name="contactPreference"
                value={option.value}
                checked={contactPreference === option.value}
                onChange={(e) => setContactPreference(e.target.value)}
                className="sr-only"
              />
              <div className="text-center">
                <div className="text-2xl mb-2">{option.icon}</div>
                <div className="font-medium text-gray-900">{option.label}</div>
                <div className="text-sm text-gray-600 mt-1">{option.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="card p-6 sm:p-8 mb-6">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="terms"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <div className="ml-3">
            <label htmlFor="terms" className="text-sm font-medium text-gray-900">
              I agree to the terms and conditions
            </label>
            <p className="text-sm text-gray-600 mt-1">
              By submitting this form, I consent to Ravana Industries processing my personal information
              to provide immigration consultation services. I understand that my information will be kept
              confidential and used only for the purpose of assessing my eligibility for immigration programs.
              I agree to be contacted via my preferred contact method.
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {t('wizard.common.previous', 'Previous')}
        </button>

        <button
          onClick={handleSubmit}
          disabled={!agreedToTerms || isSubmitting}
          className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('wizard.submit.actions.submitting', 'Submitting...')}
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
              {t('wizard.submit.actions.submitForReview', 'Submit Profile for Review')}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default Submit;
